import { Link, navigate } from 'gatsby';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';

import { HealthProfessional1Img } from '../assets/images';
import { Button, InputText } from '../components/form';
import { Container, Footer, Header } from '../components/shared';
import { LoginContext } from '../contexts/Login';
import { useServiceMethod } from '../services/common/use-service-method';
import { AuthService } from '../services/profissional/auth';
import { ButtonsWrapper, ContentWrapper, ImageWrapper, Main, SubtitleWrapper } from '../styles/home';
import { statusActionProfessionalData } from '../utils';

/**
 * Página principal para profissionais de saúde.
 *
 * @remarks
 * Esta página permite que os profissionais de saúde façam login ou criem uma conta na plataforma Lacrei Saúde.
 * Possui um fluxo de verificação de segurança para garantir que o usuário não possa acessar diretamente esta página se estiver logado,
 * a menos que faça logout.
 */

export default function ProfissionaisSaude() {
	const regexEmail = useMemo(
		() => /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
		[],
	);
	const [emailText, setEmailText] = useState('');
	const [emailTextError, setEmailTextError] = useState('');
	const [passwordText, setPasswordText] = useState('');
	const [passwordTextError, setPasswordTextError] = useState('');
	const [loading, setLoading] = useState(false);
	const [formSubmitted, setFormSubmitted] = useState(false);
	const [loadingProfessionalData, setLoadingProfessionalData] = useState(false);

	const auth = useServiceMethod(AuthService, 'login');
	const authenticated = auth.status === 'success' && auth.response !== null;
	const excepted = auth.status === 'excepted' && auth.error !== null;
	const { professionalData, getStatusProfessional } = useContext(LoginContext);

	/**
	 * Verifica se os dados de entrada não são válidos.
	 *
	 * @returns {boolean} Retorna true se os dados de entrada não forem válidos, caso contrário, retorna false.
	 */
	const isDataInputNotValid = () => {
		const errorMap = { errors: false };
		const isEmailValid = regexEmail.test(emailText.trim());

		if (!passwordText || !emailText || !isEmailValid) {
			errorMap.errors = true;
		}

		if (!passwordText) {
			setPasswordTextError('O campo “Senha” é obrigatório.');
		}

		if (!emailText) {
			setEmailTextError("O campo 'Email' é obrigatório");
		} else if (!isEmailValid) {
			setEmailTextError('Por favor, utilize um formato de e-mail válido. Por exemplo: email@dominio.com.br');
		}
		return errorMap.errors;
	};

	/**
	 * Manipula a alteração do email.
	 *
	 * @param {string} email O email inserido pelo usuário.
	 */
	const handleChangeEmail = (email) => {
		setEmailTextError(null);
		setEmailText(email);
	};

	/**
	 * Faz o login do usuário e verifica se o perfil foi deletado
	 */
	const login = useCallback(async () => {
		const authServiceToken = new AuthService();
		const tokenUser = await authServiceToken.getTokenUser(emailText, passwordText);

		const token = tokenUser.key;
		if (token) {
			const authServiceInfo = new AuthService();

			const userInfo = await authServiceInfo.infoUser(token);
			const statusProfile = userInfo.professional.profile_status;

			if (statusProfile === 'Deleted') {
				setPasswordTextError('Esta conta foi deletada!');
				setLoading(false);
				setFormSubmitted(false);
				return;
			}
		}

		await auth.call({
			email: emailText,
			password: passwordText,
		});
		setLoading(false);
		setFormSubmitted(false);
	}, [auth, emailText, passwordText]);

	/**
	 * Manipula o envio do formulário.
	 *
	 * @param {React.FormEvent<HTMLFormElement>} event O evento de envio do formulário.
	 */
	function handleSubmitForm(event) {
		event.preventDefault();

		setEmailTextError('');
		setPasswordTextError('');

		if (isDataInputNotValid()) return;

		setLoading(true);
		setFormSubmitted(true);
	}

	useEffect(() => {
		if (!emailTextError && !passwordTextError && formSubmitted) {
			login();
		}

		setFormSubmitted(false);
	}, [emailTextError, passwordTextError, formSubmitted]);

	useEffect(() => {
		if (authenticated && !loadingProfessionalData) {
			setLoadingProfessionalData(true);
			sessionStorage.setItem('authToken', auth.response.key, {
				secure: true,
				sameSite: 'strict',
			});

			(async () => {
				await getStatusProfessional();
			})();

			setLoadingProfessionalData(false);
		}

		if (excepted) {
			setEmailTextError(' ');
			setPasswordTextError('Acesso inválido. Verifique seus dados.');
		}
	}, [authenticated, loadingProfessionalData, excepted]);

	/**
	 * Possui o fluxo de fazer uma verificação de segurança, de que se o usuário tiver realizado o login, e o token estiver armazenado no SessionStorage, ele não pode acessar diretamente esta página.
	 * Para acessar, é necessário fazer o logout.
	 */

	useEffect(() => {
		const token = sessionStorage.getItem('authToken');
		const { verificationSteps } = professionalData;

		if (token && professionalData.status && authenticated) {
			if (professionalData.status !== 'approved') sessionStorage.removeItem('authToken');
			const statusUrl = statusActionProfessionalData[professionalData.status];
			const checkStep = verificationSteps.current_step === 'add_intersectionality_data';
			const stepUrl = '/saude/diversidade';
			const urlStart = checkStep && statusUrl ? stepUrl : statusUrl;
			navigate(urlStart);
		}
	}, [professionalData.status, authenticated]);

	return (
		<>
			<Helmet>
				<title>Início | Lacrei Saúde</title>
				<meta name='description' content='Página inicial da pessoa profissional.' />
			</Helmet>
			<Container>
				<Header />
				<Main>
					<ContentWrapper>
						<SubtitleWrapper>
							<h1>
								Boas-vindas à <span>Lacrei Saúde</span>
							</h1>
							<div className='divider' />
							<p>Entre ou crie sua conta Lacrei Saúde.</p>
						</SubtitleWrapper>
						<form onSubmit={handleSubmitForm}>
							<InputText
								onChange={(e) => {
									handleChangeEmail(e.target.value);
								}}
								value={emailText}
								placeholder='Digite seu e-mail'
								label='E-mail'
								error={emailTextError}
							/>
							<InputText
								onFocus={() => setPasswordTextError('')}
								onChange={(event) => setPasswordText(event.target.value)}
								value={passwordText}
								placeholder='Digite sua senha'
								label='Senha'
								error={passwordTextError}
								isPassword
							/>
							<ButtonsWrapper>
								<Button title='Entrar' type='submit' noLink shadowy loading={loading} disabled={loading} />
								<Button link='/saude/selecionar-estado/' title='Criar conta' variant='secondary' shadowy />
							</ButtonsWrapper>
							<Link to='/redefinicao-de-senha' className='forgot-password'>
								Esqueci minha senha
							</Link>
						</form>
					</ContentWrapper>
					<ImageWrapper>
						<HealthProfessional1Img
							role='img'
							alt='Profissional de saúde.'
							aria-label='Profissional de saúde.'
							className='pro-image'
						/>
						<span className='image-credits'>
							Ilustrado por{' '}
							<a className='anchor-image' target='_blank' rel='noreferrer' href='https://br.freepik.com/'>
								Freepik
							</a>
						</span>
					</ImageWrapper>
				</Main>
				<Footer hasButtonUp />
			</Container>
		</>
	);
}
